import React from 'react';
import PropTypes from 'prop-types';
import TimeAgo from '../TimeAgo/TimeAgo';
import VoteButtons from './VoteButtons';
import { cc } from '../../modules/styles';
import { getGiphyImageURL } from '../PostDetail/PostDetailHelpers';

import './PostComment.css';

/**
 * A single comment
 */

class PostComment extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      repliesExpanded: false,
    };
    this.expandReplies = this.expandReplies.bind(this);
  }

  expandReplies() {
    this.setState({ repliesExpanded: true });
  }

  render() {
    const {
      id,
      body,
      author,
      timeAgo,
      replies = [],
      byOP,
      postPermalink,
      top = false,
      className,
      vote,
      reddit,
    } = this.props;

    const hasReplies = replies && replies.length > 0;
    const showReplies =
      this.props.expandedReplies || this.state.repliesExpanded;

    return (
      <div
        className={cc(
          'PostComment',
          top ? 'PostComment--top' : 'PostComment--reply',
          showReplies ? 'PostComment--expanded' : '',
          className
        )}
      >
        <div
          style={{
            paddingLeft: '0.75rem',
            paddingRight: '0.75rem',
            marginBottom: '0.75rem',
          }}
        >
          <div
            style={{
              fontWeight: 600,
              width: '100%',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <div
              className="PostComment__author"
              style={{
                '--theme-color-light': '57%',
                '--theme-color-dark': '42%',
              }}
            >
              {byOP && (
                <span
                  className="PostComment__op-label"
                  style={{
                    '--theme-color-light': '97%',
                    '--theme-color-dark': '97%',
                  }}
                >
                  OP
                </span>
              )}
              {author}
            </div>
            <VoteButtons
              style={{
                display: 'flex',
                alignItems: 'center',
                marginLeft: 'auto',
                marginRight: '0.75rem',
              }}
              commentId={id}
              initialVote={vote}
              reddit={reddit}
            />
            <a
              href={`https://reddit.com${postPermalink}${id}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                textDecoration: 'none',
                fontWeight: 400,
                '--theme-color-light': '70%',
                '--theme-color-dark': '35%',
              }}
            >
              <TimeAgo
                timestamp={timeAgo}
                short
                style={{ display: 'inline-block', textAlign: 'right' }}
              />
            </a>
          </div>

          <CommentContent body={body} />
        </div>

        {hasReplies &&
          showReplies &&
          replies.map(reply => {
            const { id, author, timeAgo, replies, byOP, body, vote } = reply;
            return (
              <PostComment
                key={id}
                id={id}
                body={body}
                author={author}
                timeAgo={timeAgo}
                postPermalink={postPermalink}
                byOP={byOP}
                replies={replies}
                expandedReplies={this.props.expandedReplies}
                vote={vote}
                reddit={reddit}
                className="PostComment--revealed"
              />
            );
          })}

        {hasReplies && !showReplies && (
          <button
            className="PostComment__replies-button"
            onClick={this.expandReplies}
            style={{
              '--theme-color-light': '42%',
              '--theme-color-dark': '42%',
            }}
          >
            Show {replies.length > 1 ? `${replies.length} replies` : 'reply'}
          </button>
        )}
      </div>
    );
  }
}

const CommentContent = React.memo(function CommentContent({ body }) {
  return (
    <div
      className="PostComment__content UserContent"
      dangerouslySetInnerHTML={{ __html: inlineCommentImages(body) }}
    />
  );
});

PostComment.propTypes = {
  id: PropTypes.string.isRequired,
  body: PropTypes.string.isRequired,
  author: PropTypes.string.isRequired,
  timeAgo: PropTypes.number.isRequired,
  replies: PropTypes.arrayOf(PropTypes.object),
  byOP: PropTypes.bool,
  postPermalink: PropTypes.string.isRequired,
  expandedReplies: PropTypes.bool.isRequired,
  className: PropTypes.string,
};

export default PostComment;

function inlineCommentImages(body) {
  const container = document.createElement('template');
  container.innerHTML = body;

  container.content.querySelectorAll('a[href]').forEach(anchor => {
    const imageURL =
      getGiphyImageURL(anchor.href) || getRedditImageURL(anchor.href);
    if (!imageURL) return;

    const image = document.createElement('img');
    image.src = imageURL;
    image.alt = anchor.textContent || 'Comment image';
    image.loading = 'lazy';
    anchor.replaceWith(image);
  });

  return container.innerHTML;
}

function getRedditImageURL(url) {
  let parsedURL;
  try {
    parsedURL = new URL(url);
  } catch (error) {
    if (error instanceof TypeError) return null;
    throw error;
  }

  return parsedURL.protocol === 'https:' &&
    ['preview.redd.it', 'i.redd.it'].includes(parsedURL.hostname)
    ? parsedURL.href
    : null;
}
